import React, { useCallback } from "react";
import PropTypes from "prop-types";

/**
 * CarouselArrow renders a styled prev/next navigation button for a carousel.
 * @param {object} props - Component props.
 * @param {string} props.className - Additional class names from react-slick.
 * @param {Function} props.onClick - Click handler provided by react-slick.
 * @param {string} props.direction - Arrow direction: "next" or "prev".
 * @param {string|number} props.leftPos - Left position override for the prev arrow.
 * @param {string|number} props.rightPos - Right position override for the next arrow.
 * @returns {React.ReactElement} A styled carousel arrow button.
 */
export const CarouselArrow = ({ className, onClick, direction, leftPos = "-24px", rightPos = "-24px" }) => {
  /**
   * Applies box-shadow on mouse enter.
   * @param {React.MouseEvent} e - The mouse event.
   */
  const onMouseEnter = useCallback((e) => {
    e.currentTarget.style.boxShadow = "0 1px 8px 0 rgba(0, 0, 0, 0.12), 0 2px 12px 0 rgba(0, 0, 0, 0.08)";
  }, []);

  /**
   * Removes box-shadow on mouse leave.
   * @param {React.MouseEvent} e - The mouse event.
   */
  const onMouseLeave = useCallback((e) => {
    e.currentTarget.style.boxShadow = "none";
  }, []);

  return (
    <button
      type="button"
      className={`${className ?? ""} inline-flex h-auto w-auto cursor-pointer items-center justify-center gap-[10px] rounded-[32px] bg-white-900 p-[12px] text-[length:inherit] leading-normal transition-shadow duration-200 ease-in-out before:!hidden [&&]:z-50`}
      style={
        direction === "next"
          ? { right: rightPos ?? "-24px", border: "1px solid #EEE" }
          : { left: leftPos ?? "-24px", border: "1px solid #EEE" }
      }
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      aria-label={direction === "next" ? "Next slide" : "Previous slide"}
    >
      {direction === "next" ? (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M9 19.5L16.5 12L9 4.5"
            stroke="#0F172B"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M15 19.5L7.5 12L15 4.5"
            stroke="#0F172B"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </button>
  );
};

CarouselArrow.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
  direction: PropTypes.oneOf(["next", "prev"]).isRequired,
  leftPos: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rightPos: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CarouselArrow;
